import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getItemPrice, getItemsInCart } from '@next-order/next-order-engine';
import { toggleMobileCart } from 'containers/Home/actions';
import getImage from 'helpers/getImage';
import {
  buildBogoEstimateRows,
  excludeBogoEditRows,
} from 'helpers/bogoVoucherHelper';
import { engineShowSpecialItem } from 'helpers/order-engine';
import {
  detectBogoDiscount,
  getBogoSpendProgress,
} from 'helpers/order-engine/getBogoVoucherDiscount';
import useSpecialItem from 'components/ItemView/useSpecialItem';
import './BogoVoucherModal.css';

const TRANSITION_MS = 300;

const getActiveIds = (list) =>
  new Set((list || []).filter((e) => e?.isActive).map((e) => e._id));

// Sizes of `menuItem` a section accepts. Single-size simple items always
// qualify (same rule engineShowSpecialItem applies).
const getQualifyingSizes = (menuItem, section) => {
  const sizeIds = getActiveIds(section.menuSizes);
  const subModifierIds = getActiveIds(section.subModifiers);
  if (menuItem.itemType === 1) {
    const baseModifier = (menuItem.selectedModifiers || []).find(
      (modifier) => modifier._id === menuItem.baseModifierId
    );
    return (baseModifier?.subModifiers || []).filter((subModifier) =>
      subModifierIds.has(subModifier._id)
    );
  }
  const sizes = menuItem.selectedSizes || [];
  if (sizes.length === 1) return sizes;
  return sizes.filter(
    (size) => sizeIds.has(size._id) || subModifierIds.has(size._id)
  );
};

// Same rule as getPriceForDisplay: an option with variants is sold out only
// when its base and every variant are sold out.
const isOptionSoldOut = (menuItemId, option, soldOutMenuItems) => {
  const isEntrySoldOut = (variantId) =>
    (soldOutMenuItems || []).some(
      (entry) =>
        entry.menuItemId === menuItemId &&
        entry.smId === option._id &&
        (variantId ? entry.variantId === variantId : !entry.variantId)
    );
  const variants = option.variants || [];
  if (!isEntrySoldOut(null)) return false;
  return variants.every((variant) => isEntrySoldOut(variant._id));
};

const needsCustomisation = (menuItem, qualifyingSizes) =>
  menuItem.itemType === 1 ||
  (menuItem.selectedModifiers || []).length > 0 ||
  qualifyingSizes.length !== 1;

/**
 * Mobile-only BOGO voucher (type "4") Buy/Get drawer.
 *
 * Same slot model as BogoVoucherView (buy slots, then get slots; spend deals
 * grow the buy section until minSpend is met), but items are picked from a
 * list inside the drawer instead of filtering the main menu. Items that need
 * a size/modifier choice open the existing voucher item view; the drawer
 * stays mounted (hidden) meanwhile so the active slot survives.
 */
export default function BogoVoucherModal({
  voucherItem,
  selectedSpecials,
  hidden,
  storeConfig,
  hideOrder,
  addBogoVoucherItemsToOrder,
  addToBogoVoucher,
  showVoucherItem,
  setHomeState,
}) {
  const dispatch = useDispatch();
  const { addToOrderItem } = useSpecialItem();
  const halfHalfSetup = useSelector(
    (state) => state.layoutReducer.halfHalfSetup
  );
  const menuItems = useSelector((state) => state.homeReducer.menuItems);
  const soldOutMenuItems = useSelector(
    (state) => state.homeReducer.soldOutMenuItems
  );
  const currentOrder = useSelector((state) => state.homeReducer.currentOrder);
  const currency = storeConfig?.currency || '';

  const [isOpen, setIsOpen] = useState(false);
  const isClosingRef = useRef(false);
  const listRef = useRef(null);
  const preselectedVoucherIdRef = useRef(null);
  const isLoading = !!voucherItem?.isLoading;

  const sections = useMemo(
    () => voucherItem?.bogoSections || [],
    [voucherItem?.bogoSections]
  );
  const spendSection = sections.find((section) => section.isSpend);
  const getQuantity =
    sections.find((section) => section.key === 'get')?.quantity || 0;

  const editUnits = voucherItem?.editUnits;
  const cartRows = useMemo(
    () =>
      spendSection
        ? excludeBogoEditRows(
            getItemsInCart(currentOrder || {}).filter(
              (row) => !row.isSecondHalf
            ),
            editUnits
          )
        : [],
    [spendSection, currentOrder, editUnits]
  );
  const keyOffset = cartRows.reduce(
    (max, row) => Math.max(max, Number(row.primaryKey) || 0),
    0
  );

  const voucherSelections = useMemo(
    () =>
      (selectedSpecials || []).filter(
        (item) => item.voucherId === voucherItem?._id
      ),
    [selectedSpecials, voucherItem?._id]
  );

  const buySelectionIndexes = useMemo(
    () =>
      spendSection
        ? [
            ...new Set(
              voucherSelections
                .filter((item) => item.boxIndex >= getQuantity)
                .map((item) => item.boxIndex)
            ),
          ].sort((a, b) => a - b)
        : [],
    [spendSection, voucherSelections, getQuantity]
  );

  const spendProgress = useMemo(() => {
    if (!spendSection) return null;
    const buySelections = voucherSelections.filter(
      (item) => item.boxIndex >= getQuantity
    );
    return getBogoSpendProgress(voucherItem, [
      ...cartRows,
      ...buildBogoEstimateRows(buySelections, keyOffset),
    ]);
  }, [
    spendSection,
    voucherSelections,
    voucherItem,
    getQuantity,
    cartRows,
    keyOffset,
  ]);
  const isSpendMet = !!spendProgress && spendProgress.remaining <= 0;

  // Buy slots first, then get slots — boxIndex matches BogoVoucherView.
  const slots = useMemo(() => {
    const list = [];
    if (spendSection) {
      const buyIndexes = [...buySelectionIndexes];
      if (!isSpendMet) {
        buyIndexes.push(
          buyIndexes.length
            ? buyIndexes[buyIndexes.length - 1] + 1
            : getQuantity
        );
      }
      sections.forEach((section) => {
        if (section.isSpend) {
          buyIndexes.forEach((boxIndex) => list.push({ boxIndex, section }));
        } else {
          for (let i = 0; i < section.quantity; i++) {
            list.push({ boxIndex: i, section });
          }
        }
      });
      return list;
    }
    sections.forEach((section) => {
      for (let i = 0; i < section.quantity; i++) {
        list.push({ boxIndex: list.length, section });
      }
    });
    return list;
  }, [sections, spendSection, buySelectionIndexes, isSpendMet, getQuantity]);

  const getSelectionForSlot = (boxIndex) =>
    voucherSelections.find((item) => item.boxIndex === boxIndex);

  const firstEmptySlot = slots.find(
    (slot) => !getSelectionForSlot(slot.boxIndex)
  );
  const [activeBoxIndex, setActiveBoxIndex] = useState(
    () =>
      voucherItem?.initialBoxIndex ??
      firstEmptySlot?.boxIndex ??
      slots[0]?.boxIndex ??
      -1
  );
  const activeSlot =
    slots.find((slot) => slot.boxIndex === activeBoxIndex) ||
    firstEmptySlot ||
    slots[0];
  const activePosition = slots.indexOf(activeSlot);
  const activeSection = activeSlot?.section;

  // Advance to the next empty slot once the active one gets filled.
  const filledKey = voucherSelections
    .map((item) => `${item.boxIndex}:${item._id}`)
    .join('|');
  const prevFilledKeyRef = useRef(filledKey);
  useEffect(() => {
    if (prevFilledKeyRef.current === filledKey) return;
    prevFilledKeyRef.current = filledKey;
    if (!activeSlot || !getSelectionForSlot(activeSlot.boxIndex)) return;
    const ordered = [
      ...slots.slice(activePosition + 1),
      ...slots.slice(0, activePosition),
    ];
    const nextEmpty = ordered.find(
      (slot) => !getSelectionForSlot(slot.boxIndex)
    );
    if (nextEmpty) setActiveBoxIndex(nextEmpty.boxIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledKey, slots]);

  // Home reads this index when the item view hands a customised item back.
  useEffect(() => {
    setHomeState({ bogoActiveBoxIndex: activeSlot?.boxIndex ?? -1 });
    if (listRef.current) listRef.current.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlot?.boxIndex]);

  // Enter animation + keep the cart sidebar out of the way while visible.
  useEffect(() => {
    if (hidden) return undefined;
    dispatch(toggleMobileCart(false));
    const frame = requestAnimationFrame(() => setIsOpen(true));
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden]);

  const closeThen = (callback) => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsOpen(false);
    setTimeout(callback, TRANSITION_MS);
  };

  const menuItemsById = useMemo(
    () =>
      (menuItems || []).reduce((acc, item) => {
        acc[item._id] = item;
        return acc;
      }, {}),
    [menuItems]
  );

  const sectionRows = useMemo(() => {
    if (!activeSection) return [];
    return (activeSection.menuItems || [])
      .map((stub) => menuItemsById[stub._id])
      .filter(Boolean)
      .map((menuItem) => {
        const qualifyingSizes = getQualifyingSizes(menuItem, activeSection);
        const availableSizes = qualifyingSizes.filter(
          (size) => !isOptionSoldOut(menuItem._id, size, soldOutMenuItems)
        );
        const isSoldOut = !!menuItem.isSoldOut || availableSizes.length === 0;
        const prices = (isSoldOut ? qualifyingSizes : availableSizes)
          .map((size) => Number(size.price))
          .filter((price) => Number.isFinite(price));
        return {
          menuItem,
          qualifyingSizes,
          isSoldOut,
          price: prices.length ? Math.min(...prices) : 0,
        };
      })
      .filter((row) => row.qualifyingSizes.length > 0)
      .sort((a, b) => Number(a.isSoldOut) - Number(b.isSoldOut));
  }, [activeSection, menuItemsById, soldOutMenuItems]);

  const benefit = voucherItem?.benefit || {};
  const benefitType = benefit.benefitType || '3';
  const isFreeBenefit = benefitType !== '1' && benefitType !== '2';

  const getBenefitText = (section) => {
    if (benefitType === '1') {
      const cap = Number(benefit.maxDiscountAmount);
      return `${Number(benefit.discountValue) || 0}% discount${
        cap > 0 ? ` (max ${currency}${cap})` : ''
      }`;
    }
    if (benefitType === '2') {
      return `${currency}${Number(benefit.discountValue) || 0} discount`;
    }
    return section.quantity > 1 ? 'Free items' : 'Free item';
  };

  const getBenefitBadge = () => {
    if (benefitType === '1') return `${Number(benefit.discountValue) || 0}% off`;
    if (benefitType === '2')
      return `${currency}${Number(benefit.discountValue) || 0} off`;
    return 'Free';
  };

  const getSelectionPrice = (item) =>
    Number(getItemPrice(item, halfHalfSetup)) || 0;

  const filledSelections = slots
    .map((slot) => getSelectionForSlot(slot.boxIndex))
    .filter(Boolean);
  const isComplete = slots.length > 0 && filledSelections.length === slots.length;

  const { totalCost, estimatedDiscount } = useMemo(() => {
    const total = filledSelections.reduce(
      (acc, item) => acc + getSelectionPrice(item),
      0
    );
    const rows = [
      ...cartRows,
      ...buildBogoEstimateRows(filledSelections, keyOffset),
    ];
    return {
      totalCost: total,
      estimatedDiscount: detectBogoDiscount(voucherItem, rows).voucherDiscount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledKey, voucherItem, halfHalfSetup, cartRows, keyOffset]);
  const payable = Math.max(totalCost - estimatedDiscount, 0);

  const selectRow = ({ menuItem, qualifyingSizes, isSoldOut }) => {
    if (!activeSlot || isSoldOut) return;
    const { section, boxIndex } = activeSlot;
    if (needsCustomisation(menuItem, qualifyingSizes)) {
      setHomeState({ bogoActiveBoxIndex: boxIndex });
      dispatch(toggleMobileCart(true));
      showVoucherItem(menuItem, section.menuSizes, section.subModifiers);
      return;
    }
    const specialItem = engineShowSpecialItem(
      menuItem,
      section.menuSizes,
      section.subModifiers
    );
    if (!specialItem) return;
    addToBogoVoucher(addToOrderItem(specialItem, halfHalfSetup), boxIndex);
  };

  // Offers card for a specific menu item: drop it into the first Buy slot
  // once the voucher has loaded (only once per voucher).
  useEffect(() => {
    const menuItemId = voucherItem?.preselectMenuItemId;
    if (!menuItemId || isLoading || hidden) return;
    if (preselectedVoucherIdRef.current === voucherItem._id) return;
    if (activeSection?.key !== 'buy' || !sectionRows.length) return;
    preselectedVoucherIdRef.current = voucherItem._id;
    const row = sectionRows.find((r) => r.menuItem._id === menuItemId);
    if (row) selectRow(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    voucherItem?._id,
    voucherItem?.preselectMenuItemId,
    isLoading,
    hidden,
    activeSection,
    sectionRows,
  ]);

  const onFooterClick = () => {
    if (isComplete) {
      closeThen(addBogoVoucherItemsToOrder);
      return;
    }
    if (firstEmptySlot) setActiveBoxIndex(firstEmptySlot.boxIndex);
  };

  const onHeaderButtonClick = () => {
    if (activePosition > 0) {
      setActiveBoxIndex(slots[activePosition - 1].boxIndex);
      return;
    }
    closeThen(hideOrder);
  };

  const isGetStep = activeSection?.key === 'get';
  const rewardNoun = isFreeBenefit ? 'free item' : 'discounted item';

  const getInstruction = () => {
    if (isComplete) return 'All set. Add it to your order.';
    if (isGetStep) {
      const buyNames = voucherSelections
        .filter((item) => {
          const slot = slots.find((s) => s.boxIndex === item.boxIndex);
          return slot?.section.key === 'buy';
        })
        .map((item) => item.name);
      const intro = buyNames.length
        ? `${buyNames.join(', ')} ${buyNames.length > 1 ? 'are' : 'is'} in. `
        : '';
      return `${intro}Now choose the item you want ${
        isFreeBenefit ? 'free' : 'discounted'
      }.`;
    }
    if (activeSection?.isSpend) {
      return `Spend ${currency}${activeSection.minSpend} on ${
        activeSection.anyItem
          ? 'anything'
          : activeSection.description || 'selected items'
      }. The reward item does not count.`;
    }
    const buyQuantity = activeSection?.quantity || 1;
    return `Pick ${
      buyQuantity > 1 ? `${buyQuantity} items` : 'an item'
    }, then choose your ${rewardNoun}.`;
  };

  const getListMeta = () => {
    if (activeSection?.isSpend) {
      const remaining = spendProgress?.remaining ?? activeSection.minSpend;
      return remaining > 0
        ? `${currency}${Number(remaining).toFixed(2)} to go`
        : 'Spend reached';
    }
    return `Choose ${activeSection?.quantity || 1}`;
  };

  const getFooterLabel = () => {
    if (isComplete) return null;
    return firstEmptySlot?.section.key === 'get'
      ? `Choose your ${rewardNoun}`
      : 'Choose your item';
  };

  const renderSlot = (slot) => {
    const selection = getSelectionForSlot(slot.boxIndex);
    const isGet = slot.section.key === 'get';
    const isActive = slot.boxIndex === activeSlot?.boxIndex;
    const className = `bogoDrawer__slot${
      isActive ? ' bogoDrawer__slot--active' : ''
    }${selection ? '' : ' bogoDrawer__slot--empty'}`;

    if (!selection) {
      return (
        <button
          type='button'
          key={`bogo-drawer-slot-${slot.section.key}-${slot.boxIndex}`}
          className={className}
          onClick={() => setActiveBoxIndex(slot.boxIndex)}
        >
          <span className='bogoDrawer__slotPlus'>+</span>
          <span className='bogoDrawer__slotLabel'>
            {isGet ? `${isFreeBenefit ? 'Free' : 'Reward'} item` : 'Buy item'}
          </span>
        </button>
      );
    }

    const imageUrl = getImage(selection.mediaPath || selection.urlS3);
    return (
      <button
        type='button'
        key={`bogo-drawer-slot-${slot.section.key}-${slot.boxIndex}`}
        className={className}
        onClick={() => setActiveBoxIndex(slot.boxIndex)}
      >
        {imageUrl && <img src={imageUrl} alt='' />}
        <span className='bogoDrawer__slotShade' />
        <span
          className={`bogoDrawer__badge ${
            isGet ? 'bogoDrawer__badge--reward' : 'bogoDrawer__badge--buy'
          }`}
        >
          {isGet ? getBenefitBadge() : 'Buy'}
        </span>
        <span className='bogoDrawer__slotFooter'>
          <span
            className={`bogoDrawer__slotPrice${
              isGet ? ' bogoDrawer__strike' : ''
            }`}
          >
            {currency}
            {getSelectionPrice(selection).toFixed(2)}
          </span>
          <span className='bogoDrawer__slotName'>{selection.name}</span>
        </span>
      </button>
    );
  };

  const renderRow = (row) => {
    const { menuItem, price, isSoldOut } = row;
    const imageUrl = getImage(menuItem.mediaPath || menuItem.urlS3);
    const isSelected =
      getSelectionForSlot(activeSlot?.boxIndex)?._id === menuItem._id;
    return (
      <button
        type='button'
        key={`bogo-drawer-row-${menuItem._id}`}
        className={`bogoDrawer__row${
          isSoldOut ? ' bogoDrawer__row--soldOut' : ''
        }`}
        disabled={isSoldOut}
        aria-disabled={isSoldOut}
        onClick={() => selectRow(row)}
      >
        <span className='bogoDrawer__rowImage'>
          {imageUrl && <img src={imageUrl} alt='' />}
        </span>
        <span className='bogoDrawer__rowBody'>
          <span className='bogoDrawer__rowName'>{menuItem.name}</span>
          {menuItem.description && (
            <span className='bogoDrawer__rowDescription'>
              {menuItem.description}
            </span>
          )}
          <span className='bogoDrawer__rowPills'>
            <span
              className={`bogoDrawer__pill${
                isGetStep ? ' bogoDrawer__strike' : ''
              }`}
            >
              {currency}
              {price.toFixed(2)}
            </span>
            {isGetStep && !isSoldOut && (
              <span className='bogoDrawer__pill bogoDrawer__pill--reward'>
                {getBenefitBadge()}
              </span>
            )}
            {isSoldOut && (
              <span className='bogoDrawer__soldOutBadge'>Sold out</span>
            )}
          </span>
        </span>
        <span
          className={`bogoDrawer__radio${
            isSelected ? ' bogoDrawer__radio--checked' : ''
          }`}
        />
      </button>
    );
  };

  const renderHeaderSkeleton = () => (
    <div aria-busy='true' aria-label='Loading offer'>
      <span className='bogoSkeleton bogoSkeleton--title' />
      <span className='bogoSkeleton bogoSkeleton--subtitle' />
      <span className='bogoSkeleton bogoSkeleton--subtitleShort' />
      <div className='bogoDrawer__slots'>
        <span className='bogoSkeleton bogoSkeleton--slot' />
        <span className='bogoSkeleton bogoSkeleton--slot' />
      </div>
      <div className='bogoDrawer__listHeader'>
        <span className='bogoSkeleton bogoSkeleton--listTitle' />
      </div>
    </div>
  );

  const renderListSkeleton = () =>
    [...Array(4)].map((_, index) => (
      <div key={`bogo-drawer-skeleton-${index}`} className='bogoDrawer__row'>
        <span className='bogoSkeleton bogoSkeleton--rowImage' />
        <span className='bogoDrawer__rowBody'>
          <span className='bogoSkeleton bogoSkeleton--rowName' />
          <span className='bogoSkeleton bogoSkeleton--rowDescription' />
          <span className='bogoSkeleton bogoSkeleton--pill' />
        </span>
      </div>
    ));

  const title =
    voucherItem?.voucherSubHeading ||
    voucherItem?.description ||
    voucherItem?.voucherCode ||
    voucherItem?.name ||
    '';

  return createPortal(
    <div
      className={`bogoDrawer${isOpen ? ' bogoDrawer--open' : ''}`}
      style={hidden ? { display: 'none' } : undefined}
    >
      <div className='bogoDrawer__backdrop' onClick={() => closeThen(hideOrder)} />
      <div className='bogoDrawer__sheet' role='dialog' aria-modal='true'>
        <div className='bogoDrawer__header'>
          <button
            type='button'
            className='bogoDrawer__iconButton'
            aria-label={activePosition > 0 ? 'Back' : 'Close'}
            onClick={onHeaderButtonClick}
          >
            {activePosition > 0 ? (
              <svg viewBox='0 0 24 24' width='18' height='18'>
                <path
                  d='M15 5l-7 7 7 7'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg viewBox='0 0 24 24' width='18' height='18'>
                <path
                  d='M6 6l12 12M18 6L6 18'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.4'
                  strokeLinecap='round'
                />
              </svg>
            )}
          </button>
          {isLoading ? (
            renderHeaderSkeleton()
          ) : (
            <>
              <h2 className='bogoDrawer__title'>{title}</h2>
              <p className='bogoDrawer__subtitle'>{getInstruction()}</p>
              {isGetStep && (
                <p className='bogoDrawer__benefit'>
                  {getBenefitText(activeSection)}
                </p>
              )}
              <div className='bogoDrawer__slots'>{slots.map(renderSlot)}</div>
              <div className='bogoDrawer__listHeader'>
                <span className='bogoDrawer__listTitle'>
                  {isGetStep ? `Choose your ${rewardNoun}` : 'Choose your item'}
                </span>
                <span className='bogoDrawer__listMeta'>{getListMeta()}</span>
              </div>
            </>
          )}
        </div>

        <div className='bogoDrawer__list' ref={listRef}>
          {isLoading ? (
            renderListSkeleton()
          ) : sectionRows.length ? (
            sectionRows.map(renderRow)
          ) : (
            <p className='bogoDrawer__empty'>No items available.</p>
          )}
        </div>

        <div className='bogoDrawer__footer'>
          <button
            type='button'
            className='bogoDrawer__cta'
            onClick={onFooterClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className='bogoSkeleton bogoSkeleton--cta' />
            ) : isComplete ? (
              <>
                {`Add to Order - ${currency}${payable.toFixed(2)}`}
                {estimatedDiscount > 0 && (
                  <span className='bogoDrawer__ctaStrike'>
                    {currency}
                    {totalCost.toFixed(2)}
                  </span>
                )}
              </>
            ) : (
              getFooterLabel()
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
